import React, { useState, useEffect } from 'react';
import { Search, Eye, Download, Star, Plus } from 'lucide-react';
import { templateService } from '../../services/templateService';
import { toast } from 'react-hot-toast';

const TemplateCollection = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    loadPublicTemplates();
  }, [currentPage, sortBy, sortDir]);

  const loadPublicTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await templateService.getPublicTemplates(currentPage, 12, sortBy, sortDir);
      setTemplates(response.content || []);
      setTotalPages(response.totalPages || 0);
      setTotalElements(response.totalElements || 0);
    } catch (error) {
      console.error('Error loading public templates:', error);
      setError(error.response?.data?.message || error.message || 'Failed to load public templates. Please try again.');
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      loadPublicTemplates();
      return;
    }

    try {
      setLoading(true);
      setError(null);
      // Search in approved templates
      const response = await templateService.searchTemplates({
        status: 'APPROVED',
        page: 0,
        size: 100
      });
      const filteredTemplates = response.content?.filter(template =>
        template.templateName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        template.content?.toLowerCase().includes(searchTerm.toLowerCase())
      ) || [];
      setTemplates(filteredTemplates);
      setTotalPages(0);
      setTotalElements(filteredTemplates.length);
    } catch (error) {
      console.error('Error searching templates:', error);
      setError(error.response?.data?.message || error.message || 'Failed to search templates. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyTemplate = async (template) => {
    try {
      // Create a copy of the template for the customer
      const templateData = {
        templateName: `${template.templateName} (Copy)`,
        content: template.content,
        templateCategory: template.templateCategory,
        language: template.language,
        headerType: template.headerType,
        footerType: template.footerType,
        buttonType: template.buttonType,
        headerText: template.headerText,
        footerText: template.footerText,
        buttonText: template.buttonText,
        buttonUrl: template.buttonUrl,
        buttonPhoneNumber: template.buttonPhoneNumber,
        status: 'DRAFT'
      };
      
      await templateService.createTemplate(templateData);
      toast.success(`Template "${template.templateName}" copied to your list`);
    } catch (error) {
      console.error('Error copying template:', error);
      toast.error('Failed to copy template. Please try again.');
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('desc');
    }
    setCurrentPage(0);
  };

  const filteredTemplates = templates.filter(template =>
    template.templateName && template.templateName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'APPROVED':
        return 'bg-green-100 text-green-800';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      case 'DRAFT':
        return 'bg-gray-100 text-gray-800';
      case 'REJECTED':
        return 'bg-red-100 text-red-800';
      case 'DISABLED':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Collection</h1>
          <p className="text-gray-600">Discover approved templates from the community</p>
        </div>
        <div className="text-sm text-gray-500">
          {totalElements} templates available
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600">{error}</p>
          <button
            onClick={() => setError(null)}
            className="mt-2 text-sm text-red-500 hover:text-red-700 underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Search and Filters */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search templates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <button
          onClick={handleSearch}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors"
        >
          Search
        </button>
      </div>

      {/* Sort Options */}
      <div className="mb-6 flex flex-wrap gap-2">
        <span className="text-sm text-gray-600 mr-2">Sort by:</span>
        {[
          { field: 'createdAt', label: 'Date Created' },
          { field: 'templateName', label: 'Name' },
          { field: 'templateCategory', label: 'Category' }
        ].map(({ field, label }) => (
          <button
            key={field}
            onClick={() => handleSort(field)}
            className={`px-3 py-1 rounded-md text-sm transition-colors ${
              sortBy === field
                ? 'bg-blue-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {label} {sortBy === field && (sortDir === 'asc' ? '↑' : '↓')}
          </button>
        ))}
      </div>

      {/* Templates Grid */}
      <div className="flex-1 flex flex-col">
        {loading ? (
          <div className="flex-1 flex justify-center items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <div className="flex-1">
            {filteredTemplates.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredTemplates.map((template) => (
                  <div key={template.templateId} className="bg-white rounded-lg shadow-md border border-gray-200 p-4 hover:shadow-lg transition-shadow">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">{template.templateName}</h3>
                        <p className="text-sm text-gray-600 line-clamp-2">{template.templateCategory} • {template.language}</p>
                      </div>
                      <div className="flex items-center space-x-1 ml-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(template.templateStatus)}`}>
                          {template.templateStatus}
                        </span>
                      </div>
                    </div>

                    <div className="text-sm text-gray-500 mb-3">
                      <p>Created: {new Date(template.createdAt).toLocaleDateString()}</p>
                    </div>

                    <div className="mb-3">
                      <p className="text-sm text-gray-600 line-clamp-3 h-12 overflow-hidden">
                        {template.content}
                      </p>
                    </div>

                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleCopyTemplate(template)}
                        className="flex-1 bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded-md text-sm flex items-center justify-center space-x-1 transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                        <span>Copy</span>
                      </button>
                      
                      <button
                        className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-2 rounded-md text-sm flex items-center space-x-1 transition-colors"
                        title="Preview"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex-1 flex flex-col justify-center items-center">
                <div className="text-gray-400 mb-4">
                  <Star className="w-12 h-12 mx-auto" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No templates found</h3>
                <p className="text-gray-500 mb-4">
                  {searchTerm ? 'No templates match your search.' : 'No public templates are available at the moment.'}
                </p>
                {searchTerm && (
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      loadPublicTemplates();
                    }}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    Clear Search
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TemplateCollection;
